import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, vModelRadio as _vModelRadio, createTextVNode as _createTextVNode, vModelCheckbox as _vModelCheckbox } from "vue"

const _hoisted_1 = { class: "wizard" }
const _hoisted_2 = {
  key: 0,
  class: "wizard-step"
}
const _hoisted_3 = {
  key: 0,
  class: "empty-hint"
}
const _hoisted_4 = {
  key: 1,
  class: "empty-hint"
}
const _hoisted_5 = {
  key: 2,
  class: "option-list",
  role: "radiogroup",
  "aria-label": "Table regions"
}
const _hoisted_6 = ["value"]
const _hoisted_7 = { class: "option-label" }
const _hoisted_8 = { class: "option-title" }
const _hoisted_9 = { class: "option-sub" }
const _hoisted_10 = {
  key: 1,
  class: "wizard-step"
}
const _hoisted_11 = { class: "wizard-subheader" }
const _hoisted_12 = { class: "wizard-subheader-title" }
const _hoisted_13 = { class: "column-toolbar" }
const _hoisted_14 = { class: "column-toolbar-label" }
const _hoisted_15 = { class: "option-list" }
const _hoisted_16 = ["value"]
const _hoisted_17 = { class: "option-label" }
const _hoisted_18 = { class: "option-title" }
const _hoisted_19 = { class: "option-sub" }
const _hoisted_20 = { class: "wizard-actions" }
const _hoisted_21 = ["disabled"]
const _hoisted_22 = ["disabled"]

import { computed, ref, watch } from 'vue';
import type { SupportedTags, SupportedTagGroup } from '@/types/SupportedTags';
import { toLabel } from '@/services/tagLabels';
import { insertTable } from '@/services/wordInsertion';


export default /*@__PURE__*/_defineComponent({
  __name: 'InsertTableWizard',
  props: {
    tags: {}
  },
  emits: ["inserted", "status"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const step = ref<1 | 2>(1);
const regionSearch = ref('');
const selectedGroupName = ref<string | null>(null);
const checkedColumns = ref<string[]>([]);
const isInserting = ref(false);

function labelForRegion(name: string): string {
  return props.tags.groupLabels?.[name] ?? toLabel(name);
}

function labelForColumn(col: string, groupName: string): string {
  return props.tags.groupColumnLabels?.[groupName]?.[col] ?? toLabel(col);
}

const filteredGroups = computed<SupportedTagGroup[]>(() => {
  if (regionSearch.value === '') return props.tags.groups;
  const q = regionSearch.value.toLowerCase();
  return props.tags.groups.filter((g: SupportedTagGroup) =>
    g.name.toLowerCase().includes(q) || labelForRegion(g.name).toLowerCase().includes(q),
  );
});

const selectedGroup = computed<SupportedTagGroup | undefined>(() =>
  props.tags.groups.find((g: SupportedTagGroup) => g.name === selectedGroupName.value) ?? undefined,
);

watch(selectedGroup, (group) => {
  checkedColumns.value = group ? [...group.columns] : [];
});

// Source-type switch reloads tags — reset the wizard so a stale group name
// can't be sent to insertTable and Step 2 never renders against an unknown
// region.
watch(
  () => props.tags,
  () => resetWizard(),
);

function resetWizard(): void {
  step.value = 1;
  selectedGroupName.value = null;
  checkedColumns.value = [];
  regionSearch.value = '';
}

function onNext(): void {
  if (selectedGroupName.value) step.value = 2;
}

function onBack(): void {
  if (step.value === 2) step.value = 1;
}

function selectAllColumns(): void {
  const group = selectedGroup.value;
  if (group) checkedColumns.value = [...group.columns];
}

function clearColumns(): void {
  checkedColumns.value = [];
}

async function onInsert(): Promise<void> {
  if (!selectedGroup.value || checkedColumns.value.length === 0) return;
  const group = selectedGroup.value;
  // Preserve the backend column order regardless of check toggle sequence.
  const columnsInOrder = group.columns.filter((c: string) => checkedColumns.value.includes(c));
  const labels = columnsInOrder.map((c: string) => labelForColumn(c, group.name));
  isInserting.value = true;
  try {
    await insertTable(group.name, columnsInOrder, labels);
    emit('status', `Inserted table for {{${group.name}}}`);
    emit('inserted');
    // Persistent embedded flow: after a successful insert, reset for the
    // next table so users can insert a second one without navigating out.
    resetWizard();
  } catch (e) {
    emit('status', e instanceof Error ? `Insert failed: ${e.message}` : 'Insert failed');
  } finally {
    isInserting.value = false;
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (step.value === 1)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _cache[3] || (_cache[3] = _createElementVNode("div", { class: "section-header" }, [
            _createElementVNode("h3", { class: "section-title" }, "Insert Tables"),
            _createElementVNode("p", { class: "section-desc" }, " Pick a repeating region below, choose which columns to include, then Insert. ")
          ], -1 /* CACHED */)),
          _withDirectives(_createElementVNode("input", {
            id: "wizard-region-search",
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((regionSearch).value = $event)),
            type: "search",
            class: "search-input",
            placeholder: "Search table regions",
            "aria-label": "Search table regions",
            autocomplete: "off"
          }, null, 512 /* NEED_PATCH */), [
            [
              _vModelText,
              regionSearch.value,
              void 0,
              { trim: true }
            ]
          ]),
          (props.tags.groups.length === 0)
            ? (_openBlock(), _createElementBlock("p", _hoisted_3, " This source type has no table regions yet. "))
            : (filteredGroups.value.length === 0)
              ? (_openBlock(), _createElementBlock("p", _hoisted_4, " No regions match “" + _toDisplayString(regionSearch.value) + "”. ", 1 /* TEXT */))
              : (_openBlock(), _createElementBlock("div", _hoisted_5, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredGroups.value, (group) => {
                    return (_openBlock(), _createElementBlock("label", {
                      key: group.name,
                      class: "option-item"
                    }, [
                      _withDirectives(_createElementVNode("input", {
                        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((selectedGroupName).value = $event)),
                        type: "radio",
                        value: group.name
                      }, null, 8 /* PROPS */, _hoisted_6), [
                        [_vModelRadio, selectedGroupName.value]
                      ]),
                      _createElementVNode("span", _hoisted_7, [
                        _createElementVNode("span", _hoisted_8, _toDisplayString(labelForRegion(group.name)), 1 /* TEXT */),
                        _createElementVNode("span", _hoisted_9, "{{TableStart:" + _toDisplayString(group.name) + "}}", 1 /* TEXT */)
                      ])
                    ]))
                  }), 128 /* KEYED_FRAGMENT */))
                ]))
        ]))
      : (step.value === 2 && selectedGroup.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
            _createElementVNode("div", { class: "wizard-header" }, [
              _createElementVNode("button", {
                type: "button",
                class: "link-btn",
                onClick: onBack
              }, [...(_cache[4] || (_cache[4] = [
                _createElementVNode("span", { "aria-hidden": "true" }, "←", -1 /* CACHED */),
                _createTextVNode(" Back ", -1 /* CACHED */)
              ]))]),
              _cache[5] || (_cache[5] = _createElementVNode("h3", { class: "section-title" }, "Choose columns", -1 /* CACHED */))
            ]),
            _createElementVNode("div", _hoisted_11, [
              _cache[6] || (_cache[6] = _createElementVNode("span", { class: "wizard-subheader-eyebrow" }, "Region", -1 /* CACHED */)),
              _createElementVNode("span", _hoisted_12, _toDisplayString(labelForRegion(selectedGroup.value!.name)), 1 /* TEXT */)
            ]),
            _createElementVNode("div", _hoisted_13, [
              _createElementVNode("span", _hoisted_14, "Columns (" + _toDisplayString(checkedColumns.value.length) + " of " + _toDisplayString(selectedGroup.value!.columns.length) + ")", 1 /* TEXT */),
              _createElementVNode("span", { class: "column-toolbar-actions" }, [
                _createElementVNode("button", {
                  type: "button",
                  class: "link-btn",
                  onClick: selectAllColumns
                }, "Select all"),
                _cache[7] || (_cache[7] = _createElementVNode("span", { class: "link-sep" }, "|", -1 /* CACHED */)),
                _createElementVNode("button", {
                  type: "button",
                  class: "link-btn",
                  onClick: clearColumns
                }, "Clear")
              ])
            ]),
            _createElementVNode("div", _hoisted_15, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(selectedGroup.value!.columns, (col) => {
                return (_openBlock(), _createElementBlock("label", {
                  key: col,
                  class: "option-item"
                }, [
                  _withDirectives(_createElementVNode("input", {
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((checkedColumns).value = $event)),
                    type: "checkbox",
                    value: col
                  }, null, 8 /* PROPS */, _hoisted_16), [
                    [_vModelCheckbox, checkedColumns.value]
                  ]),
                  _createElementVNode("span", _hoisted_17, [
                    _createElementVNode("span", _hoisted_18, _toDisplayString(labelForColumn(col, selectedGroup.value!.name)), 1 /* TEXT */),
                    _createElementVNode("span", _hoisted_19, "{{" + _toDisplayString(col) + "}}", 1 /* TEXT */)
                  ])
                ]))
              }), 128 /* KEYED_FRAGMENT */))
            ])
          ]))
        : _createCommentVNode("v-if", true),
    _createElementVNode("div", _hoisted_20, [
      (step.value === 1)
        ? (_openBlock(), _createElementBlock("button", {
            key: 0,
            type: "button",
            class: "btn btn-primary",
            disabled: !selectedGroupName.value,
            onClick: onNext
          }, " Next ", 8 /* PROPS */, _hoisted_21))
        : (_openBlock(), _createElementBlock("button", {
            key: 1,
            type: "button",
            class: "btn btn-primary",
            disabled: checkedColumns.value.length === 0 || isInserting.value,
            onClick: onInsert
          }, _toDisplayString(isInserting.value ? 'Inserting…' : 'Insert'), 9 /* TEXT, PROPS */, _hoisted_22))
    ])
  ]))
}
}

})